import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, CheckCircle2, Circle, User, HandHeart } from "lucide-react";
import { toast } from "sonner";
import {getAuthHeaders, hasToken, BACKEND_URL} from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function EventTaskList({ eventId, isOwner }) {
  const [tasks, setTasks] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    due_date: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchTasks();
    if (isOwner) fetchGuests();
  }, [eventId]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/tasks`);
      setTasks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/guests`, authConfig());
      const data = response.data;
      setGuests(Array.isArray(data) ? data : (data?.guests || []));
    } catch (error) {
      console.error("Failed to fetch guests:", error);
      setGuests([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error("Aufgabe ist erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/tasks`,
        formData,
        authConfig()
      );

      toast.success("Aufgabe hinzugefügt!");
      setFormData({ title: "", description: "", due_date: "", priority: "medium" });
      fetchTasks();
    } catch (error) {
      console.error("Failed to add task:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleClaimTask = async (taskId) => {
    const name = prompt("Dein Name:");
    if (!name) return;

    try {
      await axios.post(
        `${API}/events/${eventId}/tasks/${taskId}/claim`,
        { name }
      );
      toast.success("Du hast die Aufgabe übernommen!");
      fetchTasks();
    } catch (error) {
      console.error("Failed to claim task:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Übernehmen");
    }
  };

  const handleAssignTask = async (taskId, guestName) => {
    try {
      await axios.post(
        `${API}/events/${eventId}/tasks/${taskId}/assign`,
        { assigned_to: guestName },
        authConfig()
      );
      toast.success(`Aufgabe an ${guestName} zugewiesen!`);
      fetchTasks();
    } catch (error) {
      console.error("Failed to assign task:", error);
      toast.error("Fehler beim Zuweisen");
    }
  };

  const handleToggle = async (taskId, completed) => {
    try {
      await axios.patch(
        `${API}/events/${eventId}/tasks/${taskId}`,
        { completed: !completed },
        authConfig()
      );
      fetchTasks();
    } catch (error) {
      console.error("Failed to toggle task:", error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(
        `${API}/events/${eventId}/tasks/${taskId}`,
        authConfig()
      );
      toast.success("Aufgabe gelöscht");
      fetchTasks();
    } catch (error) {
      console.error("Failed to delete task:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const completedCount = tasks.filter(t => t.completed).length;
  const assignedCount = tasks.filter(t => t.assigned_to).length;
  const progress = tasks.length > 0 ? (completedCount / tasks.length) * 100 : 0;

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-5 h-5 text-primary" />
          Aufgabenliste
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {isOwner
            ? "Erstelle Aufgaben, die Gäste übernehmen können"
            : "Übernimm eine Aufgabe und hilf mit!"
          }
        </p>

        {/* Stats */}
        {tasks.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="p-2 bg-secondary/20 rounded text-center">
              <p className="text-xs text-muted-foreground">Gesamt</p>
              <p className="text-lg font-bold">{tasks.length}</p>
            </div>
            <div className="p-2 bg-blue-50 rounded text-center">
              <p className="text-xs text-blue-600">Zugewiesen</p>
              <p className="text-lg font-bold text-blue-600">{assignedCount}</p>
            </div>
            <div className="p-2 bg-green-50 rounded text-center">
              <p className="text-xs text-green-600">Erledigt</p>
              <p className="text-lg font-bold text-green-600">{completedCount}</p>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {tasks.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Fortschritt</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Add Task Form (Owner only) */}
      {isOwner && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
          <div>
            <Label>Aufgabe *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Was muss erledigt werden?"
              required
            />
          </div>

          <div>
            <Label>Beschreibung (optional)</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Weitere Details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fällig am</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Priorität</Label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border rounded-md bg-background"
              >
                <option value="low">Niedrig</option>
                <option value="medium">Mittel</option>
                <option value="high">Hoch</option>
              </select>
            </div>
          </div>

          <Button type="submit" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Aufgabe hinzufügen
          </Button>
        </form>
      )}

      {/* Tasks List */}
      <div className="space-y-2">
        {tasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Aufgaben</p>
            <p className="text-xs mt-1">Füge deine erste Aufgabe hinzu!</p>
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              className={`p-4 rounded-lg border transition-all ${task.completed
                  ? 'bg-green-50 border-green-200'
                  : task.assigned_to
                    ? 'bg-blue-50 border-blue-200'
                    : task.priority === 'high'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-secondary/20'
                }`}
            >
              <div className="flex items-start gap-3">
                {isOwner && (
                  <button
                    onClick={() => handleToggle(task.id, task.completed)}
                    className="mt-0.5 flex-shrink-0"
                  >
                    {task.completed ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                )}

                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <p className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''
                        }`}>
                        {task.title}
                      </p>
                      {task.description && (
                        <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                      )}
                    </div>

                    {/* Status Badge */}
                    {task.completed ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex-shrink-0">
                        ✓ Erledigt
                      </span>
                    ) : task.assigned_to ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex-shrink-0">
                        Zugewiesen
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex-shrink-0">
                        Offen
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {task.assigned_to && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {task.assigned_to}
                      </div>
                    )}
                    {task.due_date && (
                      <div>
                        📅 {new Date(task.due_date).toLocaleDateString('de-DE')}
                      </div>
                    )}
                    {task.priority === 'high' && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full">
                        Hoch
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    {/* Guest can claim unassigned tasks */}
                    {!isOwner && !task.assigned_to && !task.completed && (
                      <Button
                        size="sm"
                        onClick={() => handleClaimTask(task.id)}
                        className="rounded-full"
                      >
                        <HandHeart className="w-3 h-3 mr-1" />
                        Ich übernehme das!
                      </Button>
                    )}

                    {/* Owner can assign to guests */}
                    {isOwner && !task.assigned_to && !task.completed && guests.length > 0 && (
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAssignTask(task.id, e.target.value);
                            e.target.value = "";
                          }
                        }}
                        className="text-xs px-2 py-1 border rounded bg-background"
                      >
                        <option value="">Gast zuweisen...</option>
                        {guests.map((guest) => (
                          <option key={guest.id} value={guest.name}>
                            {guest.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {isOwner && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(task.id)}
                    className="h-8 w-8 text-destructive flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}